import React, { useEffect, useState } from "react";
import { Hourglass, Check, Clock } from "lucide-react";
import ParagraphTag from "../../../reusebale/Paragraph";
import LabelTag from "../../../reusebale/label";
import ButtonTag from "../../../reusebale/button";
import { generateRentSchedule } from "../../../../utils/leaseHelper";
import { useDispatch } from "react-redux";
import * as LeaseAction from "../../../../store/actions/lease/LeaseAction";
import { ShowError } from "../../../../utils/ToasFunction";

const RentSchedule = ({
  formData,
  onStartDate,
  onEndDate,
  onAnnualIncrementChange,
  onRentFrequencyChange,
  onRentScheduleChange,
  onIsFirstMonthFreeChange,
}) => {
  const dispatch = useDispatch();

  const [scheduleRows, setScheduleRows] = useState([]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    if (typeof dateString === "string" && dateString.includes("T")) return dateString.split("T")[0];
    return dateString;
  };

  useEffect(() => {
    const start = formData.startDate ? formatDate(formData.startDate) : "";
    const end = formData.endDate ? formatDate(formData.endDate) : "";
    const baseRent = formData.unitInfo?.rent != null ? Number(formData.unitInfo.rent) : null;
    if (!start || !end || baseRent === null || baseRent < 0) {
      setScheduleRows([]);
      if (onRentScheduleChange) onRentScheduleChange([]);
      return;
    }
    const additionalCharges = Array.isArray(formData.additionalCharges) ? formData.additionalCharges : [];
    const isFirstMonthFree = !!formData.isFirstMonthFree;
    const annualIncrementPercentage = Number(formData.annualIncrementPercentage) || 0;
    const schedule = generateRentSchedule(
      baseRent,
      additionalCharges,
      isFirstMonthFree,
      start,
      end,
      annualIncrementPercentage
    );
    setScheduleRows(schedule);
    if (onRentScheduleChange) onRentScheduleChange(schedule);
  }, [
    formData.startDate,
    formData.endDate,
    formData.unitInfo?.rent,
    formData.additionalCharges,
    formData.isFirstMonthFree,
    formData.annualIncrementPercentage,
  ]);

  const unitSize = formData.unitInfo?.size ? Number(formData.unitInfo.size) : null;
  const additionalChargesTotal = (Array.isArray(formData.additionalCharges)
    ? formData.additionalCharges
    : []
  ).reduce((sum, c) => sum + Number(c.amount || 0), 0);

  const handleNext = (e) => {
    e.preventDefault();
    if (
      !formData.startDate ||
      !formData.endDate ||
      formData.annualIncrementPercentage === "" ||
      formData.annualIncrementPercentage === null ||
      formData.annualIncrementPercentage === undefined
    ) {
      ShowError("Please fill in Lease Terms (Start Date, End Date, Annual Increment %) before proceeding.");
      return;
    }
    if (!formData.rentFrequency) {
      ShowError("Please select Rent Deduction Frequency before proceeding.");
      return;
    }
    dispatch(LeaseAction.ChangePropertyCreateSteps(5));
  };

  return (
    <div className="w-[85%] mx-auto h-full">
      <form className="overflow-y-scroll h-[80%] panelScroll px-1">
        {/* Lease Terms: all fields in a 2x2 grid */}
        <div className="mt-4">
          <ParagraphTag
            content="Lease Terms"
            classes="text-darkColor font-popinsSemiBold text-base mb-2 text-center"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <LabelTag isStaric={false} name="Start Date" classes="!text-xs !font-popinsMedium" />
              <input
                type="date"
                className="w-full mt-1 text-sm bg-bgColor font-popinsRegular px-3 py-2 rounded-sm text-darkColor placeholder:text-zinc-700/50"
                onChange={(e) => onStartDate(e.target.value)}
                value={formatDate(formData.startDate)}
              />
            </div>
            <div>
              <LabelTag isStaric={false} name="End Date" classes="!text-xs !font-popinsMedium" />
              <input
                type="date"
                className="w-full mt-1 text-sm bg-bgColor font-popinsRegular px-3 py-2 rounded-sm text-darkColor placeholder:text-zinc-700/50"
                onChange={(e) => onEndDate(e.target.value)}
                value={formatDate(formData.endDate)}
              />
            </div>
            <div>
              <LabelTag name="Rent Deduction Frequency" isStaric={false} classes="!text-xs !font-popinsMedium" />
              <select
                value={formData.rentFrequency || "Monthly"}
                onChange={(e) => onRentFrequencyChange(e.target.value)}
                className="block w-full bg-bgColor px-3 py-2 border border-gray-300 rounded-sm shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor sm:text-sm mt-1"
              >
                <option value="Monthly">Monthly</option>
                <option value="Yearly">Yearly</option>
                <option value="One Time">One Time</option>
              </select>
            </div>
            <div>
              <LabelTag isStaric={true} name="Annual Increment (%)" classes="!text-xs !font-popinsMedium" />
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="e.g., 3.5"
                className="w-full mt-1 text-sm bg-bgColor font-popinsRegular px-3 py-2 rounded-sm text-darkColor placeholder:text-zinc-700/50"
                onChange={(e) => onAnnualIncrementChange(e.target.value)}
                value={formData.annualIncrementPercentage ?? ""}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <input
              type="checkbox"
              id="isFirstMonthFree"
              checked={!!formData.isFirstMonthFree}
              onChange={(e) => onIsFirstMonthFreeChange && onIsFirstMonthFreeChange(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-themeColor focus:ring-themeColor"
            />
            <label htmlFor="isFirstMonthFree" className="text-xs font-popinsMedium text-darkColor cursor-pointer">
              Is first month free
            </label>
          </div>
        </div>

        {/* Rent Schedule heading right over the table */}
        <div className="mt-6 text-center">
          <ParagraphTag
            content="Rent Schedule"
            classes="text-darkColor font-popinsSemiBold text-lg"
          />
          <ParagraphTag
            content="Landlord base monthly rent as described on attached Exhibit or as follows:"
            classes="text-darkColor/50 font-popinsMedium text-xs mt-1"
          />
        </div>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full border border-gray-300 text-sm font-popinsRegular">
            <thead>
              <tr className="bg-bgColor">
                <th colSpan={2} className="border border-gray-300 px-2 py-2 text-left">
                  Dates
                </th>
                <th colSpan={2} className="border border-gray-300 px-2 py-2 text-left">
                  Rate per rentable square foot (optional)
                </th>
                <th colSpan={2} className="border border-gray-300 px-2 py-2 text-left">
                  Base Monthly Rent $
                </th>
                <th className="border border-gray-300 px-2 py-2 w-10 text-center" title="Status"></th>
              </tr>
              <tr className="bg-bgColor">
                <th className="border border-gray-300 px-2 py-1 text-left text-xs">From</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs">To</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs">$/rsf / month</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs">$/rsf / year</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs">Base</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs">Total</th>
                <th className="border border-gray-300 px-2 py-1 text-center text-xs w-10"></th>
              </tr>
            </thead>
            <tbody>
              {scheduleRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="border border-gray-300 px-2 py-4 text-center text-darkColor/50">
                    Set lease terms and unit rent to see the schedule.
                  </td>
                </tr>
              ) : (
                scheduleRows.map((row) => {
                  const totalMonthlyAmount = Number(row.totalRent);
                  const baseMonthlyRent = Math.max(0, totalMonthlyAmount - additionalChargesTotal);
                  const monthlyRsf = unitSize && unitSize > 0 ? totalMonthlyAmount / unitSize : null;
                  const annualRsf = monthlyRsf != null ? monthlyRsf * 12 : null;
                  return (
                    <tr key={row.id || row.fromDate + row.toDate}>
                      <td className="border border-gray-300 px-2 py-2">{row.fromDate || "—"}</td>
                      <td className="border border-gray-300 px-2 py-2">{row.toDate || "—"}</td>
                      <td className="border border-gray-300 px-2 py-2">
                        {monthlyRsf != null ? `$${monthlyRsf.toFixed(2)}` : "—"}
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        {annualRsf != null ? `$${annualRsf.toFixed(2)}` : "—"}
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        ${baseMonthlyRent.toFixed(2)}
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        ${totalMonthlyAmount.toFixed(2)}
                      </td>
                      <td className="border border-gray-300 px-2 py-2 w-10 text-center">
                        {(() => {
                          const status = String(row.status ?? "").toLowerCase();
                          if (status === "complete" || status === "completed") {
                            return <Check className="inline-block w-4 h-4 text-green-600" aria-label="Complete" />;
                          }
                          if (status === "processing") {
                            return <Hourglass className="inline-block w-4 h-4 text-amber-500" aria-label="Processing" />;
                          }
                          return <Clock className="inline-block w-4 h-4 text-gray-400" aria-label="Pending" />;
                        })()}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </form>

      <div className="h-[12%] flex justify-center items-center">
        <div className="w-[50%] flex gap-4 justify-center">
          <ButtonTag
            onSubmit={(e) => {
              e.preventDefault();
              dispatch(LeaseAction.ChangePropertyCreateSteps(3));
            }}
            name="Back"
            classes="text-base text-center bg-gray-500 text-white"
          />
          <ButtonTag
            onSubmit={handleNext}
            name="Next"
            classes="text-base text-center bg-themeColor text-white"
          />
        </div>
      </div>
    </div>
  );
};

export default RentSchedule;
